import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Pill, Plus, Trash2, Save, X, Clock } from "lucide-react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const WEEK = [
    { i: 0, label: "Seg" }, { i: 1, label: "Ter" }, { i: 2, label: "Qua" },
    { i: 3, label: "Qui" }, { i: 4, label: "Sex" }, { i: 5, label: "Sáb" }, { i: 6, label: "Dom" },
];

export default function RemindersPage() {
    const { user } = useAuth();
    const [reminders, setReminders] = useState([]);
    const [patients, setPatients] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(newForm());

    function newForm() {
        return {
            patient_id: "",
            medication_name: "",
            dose: "",
            times_of_day: ["08:00"],
            days_of_week: [0, 1, 2, 3, 4, 5, 6],
            notes: "",
            active: true,
        };
    }

    const load = async () => {
        const [r, p] = await Promise.all([api.get("/reminders"), api.get("/patients")]);
        setReminders(r.data);
        setPatients(p.data);
        if (p.data.length > 0 && !form.patient_id) setForm((f) => ({ ...f, patient_id: p.data[0].id }));
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/reminders", form);
            toast.success("Lembrete criado");
            setShowForm(false); setForm(newForm());
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (id) => {
        await api.delete(`/reminders/${id}`);
        toast.success("Removido");
        await load();
    };

    const patientName = (id) => patients.find((p) => p.id === id)?.full_name || "—";

    const canEdit = user?.role === "doctor" || user?.role === "admin";

    return (
        <AppShell>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-secondary)] mb-1">Cuidado contínuo</div>
                    <h1 className="font-display text-3xl font-medium text-brand">Lembretes de medicamentos</h1>
                </div>
                {canEdit && (
                    <button data-testid="new-reminder-btn" onClick={() => setShowForm(true)} className="btn-primary"><Plus size={16} /> Novo lembrete</button>
                )}
            </div>

            {showForm && (
                <div className="card-earthy mb-6 relative">
                    <button onClick={() => setShowForm(false)} className="absolute top-3 right-3 text-muted-clinical hover:text-brand"><X size={16} /></button>
                    <form onSubmit={submit} className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-3">
                            <div>
                                <label className="label-clinical">Paciente</label>
                                <select data-testid="reminder-patient-select" required value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })} className="input-clinical">
                                    <option value="">Selecione...</option>
                                    {patients.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="label-clinical">Medicamento</label>
                                <input data-testid="reminder-med-input" required value={form.medication_name} onChange={(e) => setForm({ ...form, medication_name: e.target.value })} className="input-clinical" placeholder="Ex.: Losartana" />
                            </div>
                            <div>
                                <label className="label-clinical">Dose</label>
                                <input value={form.dose} onChange={(e) => setForm({ ...form, dose: e.target.value })} className="input-clinical" placeholder="Ex.: 50mg" />
                            </div>
                            <div>
                                <label className="label-clinical">Horários (separe por vírgula)</label>
                                <input value={form.times_of_day.join(", ")} onChange={(e) => setForm({ ...form, times_of_day: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className="input-clinical" placeholder="08:00, 20:00" />
                            </div>
                        </div>
                        <div>
                            <label className="label-clinical">Dias da semana</label>
                            <div className="flex gap-2">
                                {WEEK.map((d) => {
                                    const on = form.days_of_week.includes(d.i);
                                    return (
                                        <button key={d.i} type="button" onClick={() => setForm({ ...form, days_of_week: on ? form.days_of_week.filter(x => x !== d.i) : [...form.days_of_week, d.i] })} className={`text-sm px-3 py-1.5 rounded-full ${on ? "bg-brand text-white" : "bg-alt text-brand"}`}>{d.label}</button>
                                    );
                                })}
                            </div>
                        </div>
                        <div>
                            <label className="label-clinical">Observações</label>
                            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-clinical" />
                        </div>
                        <button data-testid="reminder-save-btn" className="btn-primary"><Save size={16} /> Salvar lembrete</button>
                    </form>
                </div>
            )}

            {reminders.length === 0 ? (
                <div className="card-earthy text-sm text-muted-clinical">Nenhum lembrete cadastrado.</div>
            ) : (
                <div className="space-y-3">
                    {reminders.map((r) => (
                        <div key={r.id} data-testid={`reminder-row-${r.id}`} className="card-earthy flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3 flex-1">
                                <div className="w-10 h-10 rounded-lg bg-alt flex items-center justify-center text-brand"><Pill size={18} /></div>
                                <div className="flex-1">
                                    <div className="flex items-baseline gap-2">
                                        <div className="font-medium text-brand">{r.medication_name}</div>
                                        {r.dose && <span className="text-sm text-muted-clinical">{r.dose}</span>}
                                    </div>
                                    <div className="text-xs text-muted-clinical mt-1">
                                        <Link to={`/patients/${r.patient_id}`} className="hover:underline">{patientName(r.patient_id)}</Link>
                                    </div>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {r.times_of_day.map((t) => (
                                            <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-alt text-brand-secondary flex items-center gap-1"><Clock size={10} /> {t}</span>
                                        ))}
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-alt text-brand-secondary">
                                            {r.days_of_week.length === 7 ? "Todos os dias" : `${r.days_of_week.length} dias/sem`}
                                        </span>
                                    </div>
                                    {r.notes && <p className="text-sm text-[var(--text-secondary)] mt-2">{r.notes}</p>}
                                </div>
                            </div>
                            {canEdit && (
                                <button onClick={() => remove(r.id)} data-testid={`reminder-delete-${r.id}`} className="text-[var(--status-danger)] hover:bg-[var(--status-danger-bg)] p-2 rounded"><Trash2 size={14} /></button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </AppShell>
    );
}
